import { localPartCandidates, numberedFrom } from '@domain/value-objects/EmailLocalPart';
import type { PersonNameParts } from '@domain/entities/Person';

/**
 * Elegir un correo libre, sin llamarle libre a lo que no se pudo comprobar.
 *
 * **La trampa que este archivo existe para no pisar.** Comprobar si un correo
 * está tomado es preguntar por una persona, y esa consulta puede fallar por algo
 * que no es «no existe»: las reglas acotan las lecturas de `people` por área, así
 * que buscar un correo de otra área puede devolver permiso denegado. Un `catch`
 * que tratara cualquier fallo como «no encontrado» ofrecería como libre un correo
 * que ya es de alguien — y el choque aparecería recién al crear la cuenta en
 * Workspace, o peor, no aparecería.
 *
 * Por eso hay tres estados y no dos. `unknown` no es un error que ocultar: es la
 * respuesta honesta, y la pantalla la enseña como «no se pudo comprobar» en vez
 * de como un visto bueno. Es la misma familia que ya cuesta caro en este
 * repositorio — un comando que sale en verde por no haber hecho nada.
 */
export type Availability = 'free' | 'taken' | 'unknown';

/** Cuántas alternativas libres se ofrecen. Más de tres es una lista, no una sugerencia. */
const WANTED = 3;

/** Hasta dónde llega el sufijo numérico antes de rendirse. */
const MAX_NUMBERED = 4;

export interface EmailSuggestion {
  /** El primero libre, o `null` si no se encontró ninguno. */
  readonly suggested: string | null;
  /** Los libres, en orden de preferencia. Incluye a `suggested` como primero. */
  readonly free: readonly string[];
  /** Los que ya son de alguien. Se enseñan para que se vea por qué no se eligió. */
  readonly taken: readonly string[];
  /** Hubo al menos una comprobación que no se pudo hacer. */
  readonly incomplete: boolean;
}

/**
 * Recorre los candidatos en orden y se queda con los primeros libres.
 *
 * `check` se inyecta para que esto se pruebe sin Firebase — y para que la
 * decisión de qué es «libre» viva en un solo sitio en vez de repartida entre la
 * pantalla y el adaptador.
 *
 * Para en cuanto junta tres, así que el caso normal —el patrón de la casa está
 * libre— cuesta una sola consulta y no siete.
 */
export const suggestEmail = async (
  parts: PersonNameParts,
  check: (localPart: string) => Promise<Availability>,
): Promise<EmailSuggestion> => {
  const free: string[] = [];
  const taken: string[] = [];
  let incomplete = false;

  const candidates = localPartCandidates(parts);

  for (const candidate of candidates) {
    if (free.length >= WANTED) break;
    const availability = await check(candidate);
    if (availability === 'free') free.push(candidate);
    else if (availability === 'taken') taken.push(candidate);
    else incomplete = true;
  }

  /*
   * El sufijo numérico sólo si de verdad no quedó nada. Se cuelga del patrón de
   * la casa y no del último candidato probado, porque `jose.lugardo2` se dicta
   * por teléfono y `jlugardo2` ya no dice de quién es.
   */
  const house = candidates[0];
  if (free.length === 0 && house !== undefined) {
    for (let n = 2; n < 2 + MAX_NUMBERED && free.length === 0; n += 1) {
      const numbered = numberedFrom(house, n);
      const availability = await check(numbered);
      if (availability === 'free') free.push(numbered);
      else if (availability === 'taken') taken.push(numbered);
      else incomplete = true;
    }
  }

  return { suggested: free[0] ?? null, free, taken, incomplete };
};
